import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Ban,
  Cpu,
  Database,
  Gauge,
  Globe2,
  HardDrive,
  Loader2,
  Network,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Timer,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { fetchAdminActiveBans } from '@/api/admin';
import { fetchThreatSnapshot, fetchTopIps, liftBan, manualBan } from '@/api/threat';
import { fetchUamStats, resetUamStats } from '@/api/uam';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE, useDebouncedValue } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';

const SECONDS_TO_HUMAN = (s) => {
  if (s == null || Number.isNaN(Number(s))) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600)}h`;
};

const SnapshotCard = ({ icon: Icon, label, value, sub, accent = '#FFC20E', testid }) => (
  <div className="sc-glass rounded-2xl p-4 md:p-5 relative overflow-hidden" data-testid={testid}>
    <div
      className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
      style={{ background: `radial-gradient(circle, ${accent}22, transparent 60%)` }}
    />
    <div className="relative z-10">
      <div className="flex items-center justify-between">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center border"
          style={{ background: 'rgba(255,182,18,0.06)', borderColor: 'rgba(255,182,18,0.28)' }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-3">{label}</div>
      <div className="sc-tabular font-display font-bold text-2xl md:text-3xl mt-1 leading-none" style={{ color: accent }}>
        {value}
      </div>
      {sub && <div className="mt-2 text-[11px] text-[var(--sc-text-dim)] leading-snug">{sub}</div>}
    </div>
  </div>
);

const ThreatTelemetrySubpage = () => {
  const [snap, setSnap] = useState(null);
  const [ips, setIps] = useState({ items: [], window_seconds: 300 });
  const [bans, setBans] = useState({ items: [], page: 1, total: 0, total_pages: 1 });
  const [uam, setUam] = useState(null);
  const [banPage, setBanPage] = useState(1);
  // Latest-ban-time filter on the Active Ban Directory (server-side).
  const [banDates, setBanDates] = useState(EMPTY_DATE_RANGE);
  const appliedBanDates = useDebouncedValue(banDates);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newBanIp, setNewBanIp] = useState('');

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, i, b, u] = await Promise.all([
        fetchThreatSnapshot(),
        fetchTopIps(10),
        fetchAdminActiveBans(banPage, dateRangeParams(appliedBanDates)),
        fetchUamStats(),
      ]);
      setSnap(s);
      setIps(i);
      setBans(b);
      setUam(u);
    } catch (_err) {
      toast.error('Telemetry load failed');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [banPage, appliedBanDates]);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 60000);
    return () => clearInterval(id);
  }, [loadAll]);

  const handleLift = async (ip) => {
    try {
      const r = await liftBan(ip);
      if (r.lifted) toast.success(`Ban lifted: ${ip}`);
      else toast.message(`No active ban for ${ip}`);
      loadAll();
    } catch (err) {
      toast.error(`Lift failed: ${err.message}`);
    }
  };

  const handleManualBan = async () => {
    const ip = newBanIp.trim();
    if (!ip) {
      toast.error('Enter an IP to ban');
      return;
    }
    try {
      await manualBan(ip, 'manual ban from telemetry console', 1800);
      toast.success(`Banned ${ip} for 30 minutes`);
      setNewBanIp('');
      loadAll();
    } catch (err) {
      toast.error(`Ban failed: ${err.message}`);
    }
  };

  const handleResetUamStats = async () => {
    try {
      await resetUamStats();
      toast.success('UAM counters reset');
      loadAll();
    } catch (_err) {
      toast.error('Reset failed');
    }
  };

  if (loading) {
    return (
      <div className="sc-glass rounded-2xl p-10 flex items-center justify-center text-[var(--sc-text-dim)] gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Initialising telemetry stream…
      </div>
    );
  }

  const underAttack = snap?.system_under_attack === true;
  const lagMs = snap?.event_loop?.current_lag_ms ?? 0;
  const peakMs = snap?.event_loop?.peak_lag_ms ?? 0;
  const blocksInWindow = snap?.block_window?.blocks_in_window ?? 0;
  const blocksThreshold = snap?.block_window?.threshold ?? 200;
  const totalBlocks = snap?.block_window?.total_blocks ?? 0;
  const rpsNow = snap?.rps?.current ?? 0;
  const baseline = snap?.rps?.baseline_5m_mean ?? 0;
  const memMb = snap?.memory_mb ?? 0;
  const queueDepth = snap?.queue_depth ?? 0;
  const rpsDelta = baseline > 0 ? ((rpsNow - baseline) / baseline) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="threat-subpage">
      {/* Hero status panel */}
      <div className={`sc-glass rounded-2xl p-5 md:p-6 ${underAttack ? 'sc-threat-hero--alert' : ''}`} data-testid="threat-hero">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: underAttack
                ? 'linear-gradient(180deg, #ff4d4f 0%, #b91c1c 100%)'
                : 'linear-gradient(180deg, rgba(255,182,18,0.16), rgba(255,182,18,0.02))',
              border: underAttack ? '1px solid rgba(248,113,113,0.55)' : '1px solid rgba(255,182,18,0.34)',
            }}
          >
            {underAttack ? (
              <AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.6} />
            ) : (
              <ShieldCheck className="w-5 h-5 text-[#FFC20E]" strokeWidth={2.4} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Threat Telemetry · Real-Time</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl mt-1" style={{ color: underAttack ? '#fca5a5' : '#FFC20E' }}>
              {underAttack ? '⚠️ CRITICAL ADVISORY: DDoS FLOOD DETECTED' : 'SYSTEM STATUS: STABLE'}
            </h2>
            <p className="text-[12px] text-[var(--sc-text-dim)] mt-2 max-w-2xl">
              {underAttack
                ? `Trip reason: ${snap?.reason || '—'}. Layer-7 defenses are auto-throttling abusive sources. Cool-down begins as soon as inbound floods normalise.`
                : 'All Layer-7 defenses idle. Event-loop monitor, rate limiter, and ban directory operating within nominal envelopes.'}
            </p>
          </div>
          <button
            type="button"
            onClick={loadAll}
            disabled={refreshing}
            className="sc-btn-ghost shrink-0"
            style={{ padding: '9px 14px', fontSize: 11 }}
            data-testid="threat-refresh"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Live metrics grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SnapshotCard
          testid="metric-rps"
          icon={Network}
          label="Requests / Second"
          value={rpsNow.toFixed(2)}
          sub={
            <span className="inline-flex items-center gap-1" style={{ color: rpsDelta >= 0 ? '#7dd87a' : '#ff8a8a' }}>
              {rpsDelta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(rpsDelta).toFixed(1)}% vs 5m baseline ({baseline.toFixed(2)})
            </span>
          }
        />
        <SnapshotCard
          testid="metric-lag"
          icon={Timer}
          label="Event Loop Lag"
          value={`${lagMs.toFixed(0)} ms`}
          accent={lagMs > 70 ? '#fca5a5' : '#FFC20E'}
          sub={`Peak ${peakMs.toFixed(0)} ms · ceiling 70 ms`}
        />
        <SnapshotCard testid="metric-mem" icon={HardDrive} label="Memory Footprint" value={`${memMb.toFixed(1)} MB`} sub="Resident set, this worker" />
        <SnapshotCard testid="metric-queue" icon={Database} label="Engine Queue Depth" value={queueDepth} sub="Rotator pool buffer (Redis)" />
        <SnapshotCard
          testid="metric-blocks"
          icon={Gauge}
          label="Blocks / 10 s"
          value={`${blocksInWindow} / ${blocksThreshold}`}
          accent={blocksInWindow >= blocksThreshold * 0.5 ? '#fca5a5' : '#FFC20E'}
          sub={`Trigger ≥ ${blocksThreshold} flips system_under_attack`}
        />
        <SnapshotCard
          testid="metric-total-blocks"
          icon={ShieldOff}
          label="Lifetime Blocks"
          value={totalBlocks}
          sub="All rate-limit / EL / timeout violations"
        />
        <SnapshotCard testid="metric-bans" icon={Ban} label="Active Bans" value={bans?.total ?? 0} sub="Lift below if a legitimate IP is caught" />
        <SnapshotCard testid="metric-cpu" icon={Cpu} label="Total Hits Logged" value={snap?.total_requests_seen ?? 0} sub="Lifetime ingress (this process)" />
      </div>

      {/* Top-10 IPs */}
      <div className="sc-glass rounded-2xl p-5" data-testid="top-ips-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Globe2 className="w-4 h-4 text-[#FFC20E]" />
            <div className="font-display font-bold text-lg">Top 10 High-Frequency Source IPs</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{Math.round((ips.window_seconds ?? 300) / 60)} min window</div>
        </div>
        <div className="sc-admin-table-wrap">
          <table className="sc-admin-table" data-testid="top-ips-table">
            <thead>
              <tr>
                <th>Source IP</th>
                <th className="text-right">Hits</th>
                <th className="text-right">RPS</th>
                <th>Top Endpoint</th>
                <th>User-Agent</th>
                <th>Action</th>
                <th className="text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {!ips?.items?.length ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-[var(--sc-text-mute)]">
                    No traffic in the 5-minute window yet.
                  </td>
                </tr>
              ) : (
                ips.items.map((row) => (
                  <tr key={row.ip} data-testid={`ip-row-${row.ip}`}>
                    <td className="font-semibold text-sm">{row.ip}</td>
                    <td className="text-right sc-tabular">{row.hits}</td>
                    <td className="text-right sc-tabular text-[#FFC20E]">{row.rps.toFixed(3)}</td>
                    <td className="text-[11px] text-[var(--sc-text-dim)] font-mono max-w-[260px] truncate">{row.top_endpoint}</td>
                    <td className="text-[11px] text-[var(--sc-text-mute)] max-w-[180px] truncate">{row.user_agent}</td>
                    <td>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold border ${
                          row.action === 'banned'
                            ? 'text-[#fca5a5] border-[rgba(248,113,113,0.55)] bg-[rgba(248,113,113,0.08)]'
                            : 'text-[#FFC20E] border-[rgba(255,182,18,0.42)] bg-[rgba(255,182,18,0.04)]'
                        }`}
                      >
                        {row.action}
                      </span>
                    </td>
                    <td className="text-right">
                      {row.action === 'banned' ? (
                        <button
                          type="button"
                          onClick={() => handleLift(row.ip)}
                          className="sc-btn-ghost"
                          style={{ padding: '5px 10px', fontSize: 10 }}
                          data-testid={`lift-ban-${row.ip}`}
                        >
                          Lift Ban
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setNewBanIp(row.ip);
                            toast.message(`Loaded ${row.ip} into the manual ban field below.`);
                          }}
                          className="sc-btn-ghost"
                          style={{ padding: '5px 10px', fontSize: 10 }}
                          data-testid={`select-ban-${row.ip}`}
                        >
                          Ban IP
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active bans */}
      <div className="sc-glass rounded-2xl p-5" data-testid="bans-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-[#FFC20E]" />
            <div className="font-display font-bold text-lg">Active Ban Directory</div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newBanIp}
              onChange={(e) => setNewBanIp(e.target.value)}
              placeholder="IPv4 or IPv6 address"
              className="sc-input"
              style={{ minWidth: 200 }}
              data-testid="manual-ban-input"
            />
            <button type="button" onClick={handleManualBan} className="sc-btn-gold" style={{ padding: '9px 14px', fontSize: 11 }} data-testid="manual-ban-btn">
              <Ban className="w-3.5 h-3.5" />
              Ban 30 m
            </button>
          </div>
        </div>
        <div className="mb-4 flex justify-end">
          <AdminDateRange
            wrap
            dateFrom={banDates.date_from}
            dateTo={banDates.date_to}
            onChange={(range) => {
              setBanDates(range);
              setBanPage(1);
            }}
            fromLabel="Banned From"
            toLabel="Banned To"
            testIdPrefix="bans-date"
          />
        </div>
        <div className="sc-admin-table-wrap">
          <table className="sc-admin-table" data-testid="bans-table">
            <thead>
              <tr>
                <th>Banned IP</th>
                <th>Reason</th>
                <th>Triggered</th>
                <th>Remaining</th>
                <th>Actor</th>
                <th className="text-right">Lift Ban</th>
              </tr>
            </thead>
            <tbody>
              {!bans?.items?.length ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-[var(--sc-text-mute)]">
                    No active bans. The directory is clean.
                  </td>
                </tr>
              ) : (
                bans.items.map((row) => {
                  const remainingS = Math.max(0, (row.expires_at || 0) - Date.now() / 1000);
                  return (
                    <tr key={row.ip} data-testid={`ban-row-${row.ip}`}>
                      <td className="font-semibold text-sm">{row.ip}</td>
                      <td className="text-[11px] text-[var(--sc-text-dim)]">{row.reason}</td>
                      <td className="text-[11px] text-[var(--sc-text-mute)]">
                        {row.banned_at ? new Date(row.banned_at * 1000).toISOString().slice(0, 19).replace('T', ' ') : '—'}
                      </td>
                      <td className="text-[11px] sc-tabular">{SECONDS_TO_HUMAN(remainingS)}</td>
                      <td className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">{row.actor || 'system'}</td>
                      <td className="text-right">
                        <button
                          type="button"
                          onClick={() => handleLift(row.ip)}
                          className="sc-btn-ghost"
                          style={{ padding: '5px 10px', fontSize: 10 }}
                          data-testid={`bans-lift-${row.ip}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Lift Ban
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <AdminPagination page={bans.page} totalPages={bans.total_pages} total={bans.total} onChange={setBanPage} />
      </div>

      {/* UAM (Under-Attack Mode) live counters */}
      <div className="sc-glass rounded-2xl p-5" data-testid="uam-stats-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className={`w-4 h-4 ${uam?.enabled ? 'text-[#fca5a5]' : 'text-[#FFC20E]'}`} />
            <div className="font-display font-bold text-lg">Under-Attack Mode (UAM)</div>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold border ${
                uam?.enabled
                  ? 'text-[#fca5a5] border-[rgba(248,113,113,0.55)] bg-[rgba(248,113,113,0.08)]'
                  : 'text-[#FFC20E] border-[rgba(255,182,18,0.42)] bg-[rgba(255,182,18,0.04)]'
              }`}
              data-testid="uam-state-pill"
            >
              {uam?.enabled ? 'ARMED' : 'STAND-DOWN'}
            </span>
          </div>
          <button type="button" onClick={handleResetUamStats} className="sc-btn-ghost" style={{ padding: '7px 12px', fontSize: 10 }} data-testid="uam-reset">
            Reset counters
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SnapshotCard
            testid="uam-issued"
            icon={ShieldAlert}
            label="Challenges Issued"
            value={uam?.counters?.challenges_issued ?? 0}
            sub="Total challenge tokens distributed"
          />
          <SnapshotCard
            testid="uam-passed"
            icon={ShieldCheck}
            label="Verifications Passed"
            value={uam?.counters?.verifications_passed ?? 0}
            accent="#7dd87a"
            sub={`Pass rate ${uam?.counters?.pass_rate_pct ?? 0}%`}
          />
          <SnapshotCard
            testid="uam-failed"
            icon={ShieldOff}
            label="Verifications Failed"
            value={uam?.counters?.verifications_failed ?? 0}
            accent="#fca5a5"
            sub="Wrong PoW or no checkbox"
          />
          <SnapshotCard testid="uam-gated" icon={Ban} label="Requests Gated" value={uam?.counters?.requests_gated ?? 0} sub="/api calls bounced by the gate" />
        </div>
        <div className="mt-4 text-[11px] text-[var(--sc-text-dim)] grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            Difficulty: <span className="text-[#FFC20E] font-semibold">{uam?.difficulty ?? 5}</span>
          </div>
          <div>
            Pass-cookie TTL: <span className="text-[#FFC20E] font-semibold">{Math.round((uam?.cookie_ttl_seconds ?? 1800) / 60)} min</span>
          </div>
          <div>
            Arm/disarm in <span className="text-[#FFC20E] font-semibold">System Variable Governor</span>.
          </div>
        </div>
      </div>

      {/* Step-6A.2 — Reinforcement Layers live counters */}
      <div className="sc-glass rounded-2xl p-5" data-testid="reinforcements-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#FFC20E]" />
            <div className="font-display font-bold text-lg">Defense Reinforcement Layers</div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Step-6A.2</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SnapshotCard
            testid="layer-honeypot"
            icon={ShieldOff}
            label="Honeypot Auto-Bans"
            value={snap?.defense_layers?.honeypot_blocks ?? 0}
            sub="Scanners caught in trap routes"
          />
          <SnapshotCard
            testid="layer-geo"
            icon={Globe2}
            label="Geo-velocity Blocks"
            value={snap?.defense_layers?.geo_velocity_blocks ?? 0}
            sub="Credential-stuffing patterns"
          />
          <SnapshotCard
            testid="layer-writecap"
            icon={Gauge}
            label="User Write-Cap Blocks"
            value={snap?.defense_layers?.user_write_cap_blocks ?? 0}
            sub="30 mutations / 60s / account"
          />
          <SnapshotCard
            testid="layer-captcha"
            icon={ShieldAlert}
            label="CAPTCHA Failures"
            value={snap?.defense_layers?.captcha_blocks ?? 0}
            accent={snap?.defense_layers?.captcha_configured ? '#FFC20E' : '#8a8a8e'}
            sub={snap?.defense_layers?.captcha_configured ? 'Turnstile active' : 'Turnstile dormant — add env keys'}
          />
          <SnapshotCard
            testid="layer-conn"
            icon={Network}
            label="In-flight Connections"
            value={snap?.defense_layers?.inflight_connections_total ?? 0}
            sub="All active /api/* requests"
          />
          <SnapshotCard
            testid="layer-refresh"
            icon={Timer}
            label="Token Refreshes"
            value={snap?.defense_layers?.refresh_count_lifetime ?? 0}
            sub="Silent JWT rotations served"
          />
          <SnapshotCard testid="layer-rps2" icon={Cpu} label="Total Hits Tracked" value={snap?.total_requests_seen ?? 0} sub="5-min rolling window log" />
          <SnapshotCard
            testid="layer-mem2"
            icon={HardDrive}
            label="Memory (live)"
            value={`${(snap?.memory_mb ?? 0).toFixed(1)} MB`}
            sub="Resident set, this worker"
          />
        </div>
      </div>

      {/* Layer-7 module status footer */}
      <div className="sc-glass-strong rounded-2xl p-5" data-testid="layer7-status">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-2 flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-[#FFC20E]" /> Layer-7 Modules Online
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[12px] text-[var(--sc-text-dim)]">
          <li>✅ Event-loop lag monitor — 70 ms ceiling, 50 ms sampler</li>
          <li>✅ Sliding-window Redis rate limiter — public 60/60s, sensitive 5/60s</li>
          <li>✅ Body clamp — 1 MB hard ceiling (HTTP 413)</li>
          <li>✅ Request lifetime — 30 s hard cap (HTTP 503)</li>
          <li>✅ Anti-ReDoS regex filter</li>
          <li>✅ IP ban registry — Redis-backed, 30 min / 24 h TTL</li>
          <li>✅ Threat state engine — 200 blocks / 10 s → attack mode</li>
          <li>✅ Telemetry ingestion — 5 min rolling IP log</li>
          <li>✅ Under-Attack Mode (UAM) — PoW + checkbox gate</li>
          <li>✅ Honeypot routes — 24 h auto-ban on probe</li>
          <li>✅ Per-IP concurrent-connection cap (8 max)</li>
          <li>✅ Geo-velocity throttle — 3 /16 prefixes / 5 min / account</li>
          <li>✅ Per-user write rate-cap — 30 mutations / 60 s</li>
          <li>✅ Pydantic strict-mode validation — extra fields rejected</li>
          <li>✅ Short-lived JWT (15 min) + 30-day refresh cookie</li>
          <li>
            {snap?.defense_layers?.captcha_configured ? '✅' : '⚠️'} Cloudflare Turnstile —{' '}
            {snap?.defense_layers?.captcha_configured ? 'active' : 'dormant (configure env keys)'}
          </li>
          <li>✅ Reserved Mongo connection pool for admin routes (25 %)</li>
        </ul>
      </div>
    </div>
  );
};

export default ThreatTelemetrySubpage;
